import { EdjPilotRestricted } from '@/shared/components/edj-pilot-restricted';
import { PolicyInsight } from '@/components/policy-insight/PolicyInsight';
import {
  ProjectionDetailsContent,
  useHasProjectionView,
} from '@/features/projection-details';
import { readSourceQuoteField } from '@/components/policy-insight/policyInsightUtils';
import { useFundingSourceDelete } from '@/hooks/useFundingSourceDelete';
import { useFundingSourceEditGuard } from '@/features/shop-insurance/hooks/use-funding-source-edit-guards';
import {
  selectClient,
  selectClientCalculationSettings,
  selectClientIsLoading,
  selectPolicyApplications,
  selectSession,
} from '@/model/selectors';
import {
  FundingSourceCostSlideOutContent,
  FundingSourceLtcPayoutSlideOutContent,
  FundingSourceTotalPayoutSlideOutContent,
  InsuranceWhiteBoxSlideOutContent,
  ROIGuidance,
  ROISlideOutContent,
  ReturnOnInvestmentCalculationOptions,
  SheetModal,
  SlideOutSheet,
  dispatchPolicyUpdate,
  putFundingSourcesForClientByClientIdRequest,
} from '@client';
import {
  putPolicyApplicationForClientByClientIdRequest,
  resetPolicyApplicationStateStatus,
  selectLastCreatedApplicationId,
  selectPolicyApplicationsStatus,
} from '@client/model';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip as DsTooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';
import { Tooltip } from '@mui/material';
import {
  FundingPolicyType,
  IRR_SUSPECT_EXPLANATION,
  IRR_SUSPECT_EXPLANATION_COMBINED,
  SessionType,
  formatLtcCostDisplay,
  formatROI,
  fundingPolicyTypeDefs,
  getPolicyName,
  isJointPolicy,
  isNullOrUndefined,
  newUuid,
  zipCodeToState,
} from '@shared';
import { useLtcCostDisplayPrecision } from '@client/hooks/useLtcCostDisplayPrecision';
import { getProductAvailableStates } from '@shared/carrierStateAvailability';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { isPolicyEligibleForApplication } from '@shared/policy-application/policyApplicationDefs';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import produce from 'immer';
import { ChevronRight, Columns3, Loader, Pencil, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { CgAsterisk } from 'react-icons/cg';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { cn } from '../lib/utils';
import { DataTable } from './DataTable';
import { SortHeader, renderHeaderCell, renderTableCell } from './DataTableColumn';
import {
  DebugIrrAction,
  DeleteAction,
  EditAction,
  FundingSourceActionMenu,
  MirrorToPartnerAction,
  UnlinkAction,
} from './FundingSourceActionMenu';
import { useVehiclePartnerActions } from '@/hooks/use-vehicle-partner-actions';
import { FundingSourceDeleteDialogs } from './FundingSourceDeleteDialogs';
import { IrrCashflowDebugModal } from './IrrCashflowDebugModal';
import { PoliciesComparisonModal } from '@/features/policies-comparison';
import { PoliciesComparisonModal as PoliciesComparisonModalLegacy } from './PoliciesComparisonModal';
import { RenamePolicyDialog } from '@/shared/components/rename-policy-dialog';
import { StateOfResidenceSelector } from './StateOfResidenceSelector';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { PRODUCTS_BY_POLICY_TYPE } from '@client/lib/nyc-lif-demo';
import { SendAnonymousPUIButton } from '@client/features/anonymous-pui';
import { UnderwritingStatus } from '@client/features/underwriting-eligibility';

export type FundingTableRow = {
  id: string;
  type: 'selfFunding' | 'annuity' | 'insurance' | 'totals';
  show: boolean | null | 0;
  fundingSourceLabel: string;
  totalCost: number;
  ltcPayOut: number;
  partnerLtcPayOut?: number;
  nonLtcPayOut: number;
  totalPayOut: number;
  projectedReturnOnInvestment: number;
  projectedCompoundAnnualGrowthRate: number;
  projectedInternalRateOfReturn: number | undefined;
  isIrrSuspect?: boolean;
  investmentPeriodYears?: number;
  ltcCostCoveragePercent: number;
  openModal: (props: any) => void;
  deleteFundingSourceCallBack: () => void;
  fundingSourceId?: string;
  isDeactivated?: boolean;
  addedByAdvisorFullName?: string;
  addedViaShopInsurance?: boolean;
  isJointPolicy?: boolean;
  policyAddedAt?: number;
  productName?: string;
  quoteId?: string;
  policyType?: FundingPolicyType | null;
  isEligibleForApplication?: boolean;
  partnerAction?: 'mirrored' | null;
  splitRatio?: number | null;
  partnerCost?: number;
  partnerFirstName?: string;
  onUnlink?: () => void;
  onMirrorToPartner?: () => void;
  onRename?: () => void;
};

const columnHelper = createColumnHelper<FundingTableRow>();

type FundingSourceTableProps = {
  setOpenModal: {
    [fundingSourceLabel: keyof FundingSources]: (...params: any[]) => void;
  };
  handleShowDownloadReport: () => void;
  className?: string;
  primaryButton?: React.ReactNode;
};

type CellStyleProps = {
  isTotal: boolean;
  color?: string;
  className?: string;
};

const getCellClassName = ({ isTotal, color, className }: CellStyleProps) => {
  return cn(className, color, isTotal && 'font-bold');
};

const renderLtcCostCell = (value: number, props: CellStyleProps) => {
  return (
    <span className={getCellClassName(props)}>
      <LtcCostDisplay value={value} />
    </span>
  );
};

const renderROICell = (value: string, props: CellStyleProps) => {
  return <span className={getCellClassName(props)}>{value}</span>;
};

function RecentlyAddedBadge() {
  return (
    <span className="text-xs font-medium text-gray-500">
      · Recently Added
    </span>
  );
}

function RenamePolicyButton({ onClick }: { onClick: () => void }) {
  return (
    <DsTooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Rename policy"
          className="text-muted-foreground transition-colors hover:text-primary"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            onClick();
          }}
        >
          <Pencil className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">Rename policy</TooltipContent>
    </DsTooltip>
  );
}

function PartnerActionBadge({ partnerFirstName, splitRatio }: { partnerFirstName: string; splitRatio?: number | null }) {
  const label = splitRatio != null
    ? `Split ${splitRatio}/${100 - splitRatio} with ${partnerFirstName}`
    : `Mirrored with ${partnerFirstName}`;

  return (
    <span className="inline-flex w-max items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
      {label}
    </span>
  );
}

export function FundingTable({
  setOpenModal,
  className,
  primaryButton,
  handleShowDownloadReport,
}: FundingSourceTableProps) {
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient)!;
  const clientIsLoading = useSelector(selectClientIsLoading);
  const policyApplications = useSelector(selectPolicyApplications) || [];
  const session = useSelector(selectSession);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const isWizardMode = session.sessionType === SessionType.wizard;
  const isAdvisor =
    session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard;

  // Start Application state
  const [showStateSelector, setShowStateSelector] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<string>('');
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [renamePolicyFundingSourceId, setRenamePolicyFundingSourceId] = useState<string | null>(
    null,
  );
  const [activePolicyInsightFundingSourceId, setActivePolicyInsightFundingSourceId] = useState<
    string | null
  >(null);
  const policyApplicationStatus = useSelector(selectPolicyApplicationsStatus);
  const lastCreatedApplicationId = useSelector(selectLastCreatedApplicationId);
  const isCreatingPolicyApplication = policyApplicationStatus === 'creatingPolicyApplication';

  const clientZipCode = client?.intakeSurvey?.clientZipCode;
  const stateFromZipCode = clientZipCode ? zipCodeToState(clientZipCode)?.stateAbbreviation : '';

  // Initialize selected state when state selector opens - auto-select from zipcode
  useEffect(() => {
    if (showStateSelector && stateFromZipCode) {
      setSelectedState(stateFromZipCode);
    }
  }, [showStateSelector, stateFromZipCode]);

  // Handle navigation after application creation
  useEffect(() => {
    if (
      policyApplicationStatus === 'creatingPolicyApplicationComplete' &&
      lastCreatedApplicationId
    ) {
      dispatch(resetPolicyApplicationStateStatus());
      navigate(`/clients/${client.clientId}/policy-application/${lastCreatedApplicationId}`);
    }
  }, [policyApplicationStatus, lastCreatedApplicationId, client.clientId, dispatch, navigate]);

  const handleStartApplication = (row: FundingTableRow) => {
    if (!row.isEligibleForApplication || !row.policyType || !row.fundingSourceId) return;

    // Check if there's already an application for this funding source
    const existingApplication = policyApplications.find(
      app => app.fundingSourceId === row.fundingSourceId,
    );

    // If an application exists, use its state and navigate directly
    if (existingApplication && existingApplication.stateOfResidenceAbbreviation) {
      navigate(
        `/clients/${client.clientId}/policy-application/${existingApplication.clientPolicyApplicationId}`,
      );
      return;
    }

    // If state selector not shown yet, show it
    if (showStateSelector !== row.id) {
      setShowStateSelector(row.id);
      return;
    }

    // If state is selected, create the application
    if (selectedState && row.policyType) {
      const clientPolicyApplicationId = newUuid();
      dispatch(
        putPolicyApplicationForClientByClientIdRequest({
          clientPolicyApplicationId,
          clientId: client.clientId,
          advisorId: client.advisorId,
          partnerClientId: client.partnerClientId,
          fundingPolicyType: row.policyType,
          fundingSourceId: row.fundingSourceId,
          stateOfResidenceAbbreviation: selectedState,
          status: 'not_started',
        }),
      );
      // Reset state after dispatching
      setShowStateSelector(null);
      setSelectedState('');
    }
  };

  const { partnerFirstName, handleMirrorToPartner, handleUnlink, handleDeleteMirrored } = useVehiclePartnerActions();
  const showPartnerSync = Boolean(partnerFirstName) && flags.partnerFundingSplit === true;

  // Delete logic handled by custom hook
  const {
    showDeleteConfirmation,
    showDeleteBlockedDialog,
    setShowDeleteBlockedDialog,
    deleteBlockedReason,
    showJointPolicyDeleteDialog,
    jointPolicyDeleteMode,
    setJointPolicyDeleteMode,
    showMirroredDeleteDialog,
    mirroredDeleteMode,
    setMirroredDeleteMode,
    deleteSelfFundingByFundingSourceIdCallBack,
    deleteAnnuityCallBack,
    deletePolicyByFundingSourceIdCallBack,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleJointPolicyDeleteCancel,
    handleJointPolicyDeleteConfirm,
    handleMirroredDeleteCancel,
    handleMirroredDeleteConfirm,
  } = useFundingSourceDelete(client, policyApplications, handleDeleteMirrored);

  const { showEditBlockedDialog, setShowEditBlockedDialog, editBlockedReason, guardedEdit } =
    useFundingSourceEditGuard(policyApplications);

  const {
    fundingSources: {
      annuity,
      combinedFundingTotalCost,
      combinedProjectedCostCoverage,
      combinedNonLtcPayout,
      combinedTotalValue,
      combinedProjectedReturnOnInvestment,
      combinedProjectedCompoundAnnualGrowthRate,
      combinedProjectedInternalRateOfReturn,
      combinedIsIrrSuspect,
      combinedInvestmentPeriodYears,
    },
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    intakeSurvey: { clientHasStartedLtc },
    multipleFundingSources,
    fundingSources,
  } = client;

  const clientHasMultipleJointPolicies = useMemo(() => {
    return checkForMultipleJointPolicies(multipleFundingSources);
  }, [multipleFundingSources]);

  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const [returnOnInvestmentState, setReturnOnInvestmentState] =
    useState<ReturnOnInvestmentCalculationType>(
      clientCalculationSettings?.returnOnInvestmentCalculationType ?? 'total',
    );

  useEffect(() => {
    const fromServer = clientCalculationSettings?.returnOnInvestmentCalculationType;
    if (fromServer !== undefined && fromServer !== null) {
      setReturnOnInvestmentState(fromServer);
    }
  }, [clientCalculationSettings?.returnOnInvestmentCalculationType]);

  const roiColumnHeaderLabel = returnOnInvestmentState === 'total' ? 'ROI' : 'Annual ROI';

  const hasActiveLtcPolicies = useMemo(() => {
    return Object.values(multipleFundingSources.ltcPolicy).some(policy => !policy.isDeactivated);
  }, [multipleFundingSources.ltcPolicy]);

  const activePolicyInsightSheetFundingSourceId =
    activePolicyInsightFundingSourceId &&
    multipleFundingSources.ltcPolicy[activePolicyInsightFundingSourceId]
      ? activePolicyInsightFundingSourceId
      : null;

  useEffect(() => {
    if (activePolicyInsightFundingSourceId && !activePolicyInsightSheetFundingSourceId) {
      setActivePolicyInsightFundingSourceId(null);
    }
  }, [activePolicyInsightFundingSourceId, activePolicyInsightSheetFundingSourceId]);

  const mostRecentPolicyTimestamp = useMemo(() => {
    const policies = Object.values(multipleFundingSources.ltcPolicy);

    if (policies.length === 0) return null;

    const timestamps = policies
      .map(policy => policy.policyAddedAt)
      .filter((timestamp): timestamp is number => timestamp !== undefined && timestamp !== null);

    if (timestamps.length === 0) return null;

    return Math.max(...timestamps);
  }, [multipleFundingSources.ltcPolicy]);

  const toggleFundingSourceCallback = (fundingSourceRow: FundingTableRow, newStatus: boolean) => {
    const { type, fundingSourceId } = fundingSourceRow;
    switch (type) {
      case 'insurance':
      case 'selfFunding':
        if (fundingSourceId)
          dispatchPolicyUpdate(
            dispatch,
            client.clientId,
            fundingSourceId,
            multipleFundingSources,
            'isDeactivated',
            newStatus,
          );
        break;
      case 'annuity':
        const updatedFundingSources = produce(fundingSources, draft => {
          draft[type].isDeactivated = newStatus;
        });
        dispatch(
          putFundingSourcesForClientByClientIdRequest({
            clientId: client.clientId,
            fundingSources: updatedFundingSources,
          }),
        );
        break;
    }
  };

  // Create table data array with totals
  const data: FundingTableRow[] = [
    ...createSelfFundingRows({
      selfFundingSources: multipleFundingSources.selfFunding,
      allPhaseInflatedProfessionalShareCost,
      setOpenModal,
      onDelete: deleteSelfFundingByFundingSourceIdCallBack,
      onMirrorToPartner: showPartnerSync ? handleMirrorToPartner : undefined,
      onUnlink: showPartnerSync ? handleUnlink : undefined,
      partnerFirstName: showPartnerSync ? partnerFirstName : undefined,
    }),
    ...createInsuranceRows({
      policies: multipleFundingSources.ltcPolicy,
      clientHasStartedLtc,
      allPhaseInflatedProfessionalShareCost,
      setOpenModal,
      onDelete: deletePolicyByFundingSourceIdCallBack,
      onRename: setRenamePolicyFundingSourceId,
      mutableClientPartner: client.mutableClientPartner,
    }),
    ...createAnnuityRows({
      annuity,
      allPhaseInflatedProfessionalShareCost,
      setOpenModal,
      onDelete: () => deleteAnnuityCallBack(client.fundingSources),
    }),
    createTotalsRow({
      combinedFundingTotalCost: combinedFundingTotalCost ?? 0,
      combinedProjectedCostCoverage: combinedProjectedCostCoverage ?? 0,
      combinedNonLtcPayout: combinedNonLtcPayout ?? 0,
      combinedTotalValue: combinedTotalValue ?? 0,
      combinedProjectedReturnOnInvestment: combinedProjectedReturnOnInvestment ?? 0,
      combinedProjectedCompoundAnnualGrowthRate: combinedProjectedCompoundAnnualGrowthRate ?? 0,
      combinedProjectedInternalRateOfReturn: combinedProjectedInternalRateOfReturn,
      combinedIsIrrSuspect: combinedIsIrrSuspect,
      combinedInvestmentPeriodYears: combinedInvestmentPeriodYears ?? 0,
    }),
  ].filter(row => row.show);

  const showTableSkeleton = clientIsLoading && data.length === 0;

  useEffect(() => {
    if (data.length > 0) {
      handleShowDownloadReport();
    }
  }, [data.length, handleShowDownloadReport]);

  const openPolicyInsightSlideOut = (fundingSourceId: string) => {
    setActivePolicyInsightFundingSourceId(fundingSourceId);
  };

  const closePolicyInsightSlideOut = () => {
    setActivePolicyInsightFundingSourceId(null);
  };

  // Column Defs
  const columnDefinitions = [
    columnHelper.accessor('fundingSourceLabel', {
      id: 'fundingSourceLabel',
      meta: { label: 'Funding Type' },
      size: 280,
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
          >
            Funding Type
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        const row = info.row.original;
        const maskedRow = clientHasMultipleJointPolicies && row.isDeactivated;
        const showRecentlyAddedBadge =
          row.type === 'insurance' && row.policyAddedAt === mostRecentPolicyTimestamp;
        const showUnderwritingBadge = row.type === 'insurance' && row.addedViaShopInsurance;
        const isEligible = row.isEligibleForApplication && row.type === 'insurance';

        const labelContent =
          row.type === 'totals' ? (
            <span className="font-bold">{value}</span>
          ) : row.type === 'insurance' && !maskedRow ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <InlineWhiteBoxSlideOutButton
                  fundingSourceId={row.fundingSourceId!}
                  onOpen={openPolicyInsightSlideOut}
                >
                  <span className="hover:underline">
                    {flags.nycLifeDemo ? PRODUCTS_BY_POLICY_TYPE[row.policyType || 2919] : value}
                  </span>
                </InlineWhiteBoxSlideOutButton>
                {row.onRename && !flags.nycLifeDemo && (
                  <RenamePolicyButton onClick={row.onRename} />
                )}
              </div>
              {(showRecentlyAddedBadge || showUnderwritingBadge) && (
                <div className="flex flex-wrap items-center gap-1">
                  {showRecentlyAddedBadge && <RecentlyAddedBadge />}
                  {showUnderwritingBadge && (
                    <UnderwritingStatus
                      productName={row.productName!}
                      fundingPolicyType={row.policyType!}
                      policyType={row.isJointPolicy ? 'Joint Life' : undefined}
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span>{value}</span>
                {showRecentlyAddedBadge && <RecentlyAddedBadge />}
              </div>
              <div className="flex flex-wrap gap-1">
                {row.partnerAction === 'mirrored' && row.partnerFirstName && (
                  <PartnerActionBadge partnerFirstName={row.partnerFirstName} splitRatio={row.splitRatio} />
                )}
                {showUnderwritingBadge && (
                  <UnderwritingStatus
                    productName={row.productName!}
                    fundingPolicyType={row.policyType!}
                    policyType={row.isJointPolicy ? 'Joint Life' : undefined}
                  />
                )}
              </div>
            </div>
          );

        // Wrap with Popover if eligible for application
        if (isEligible && row.type === 'insurance' && !maskedRow) {
          // Check if there's already an application for this funding source
          const existingApplication = policyApplications.find(
            app => app.fundingSourceId === row.fundingSourceId,
          );
          const hasExistingApplication =
            !!existingApplication && !!existingApplication.stateOfResidenceAbbreviation;

          const showSelector = showStateSelector === row.id && !hasExistingApplication;
          const isOpen = openPopoverId === row.id || showSelector;

          return renderTableCell(
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <InlineWhiteBoxSlideOutButton
                  fundingSourceId={row.fundingSourceId!}
                  onOpen={openPolicyInsightSlideOut}
                >
                  <span className="hover:underline">
                    {flags.nycLifeDemo ? PRODUCTS_BY_POLICY_TYPE[row.policyType || 2919] : value}
                  </span>
                </InlineWhiteBoxSlideOutButton>
                {row.onRename && !flags.nycLifeDemo && (
                  <RenamePolicyButton onClick={row.onRename} />
                )}
              </div>
              {(showRecentlyAddedBadge || showUnderwritingBadge) && (
                <div className="flex flex-wrap items-center gap-1">
                  {showRecentlyAddedBadge && <RecentlyAddedBadge />}
                  {showUnderwritingBadge && (
                    <UnderwritingStatus
                      productName={row.productName!}
                      fundingPolicyType={row.policyType!}
                      policyType={row.isJointPolicy ? 'Joint Life' : undefined}
                    />
                  )}
                </div>
              )}
              {!flags.nycLifeDemo && (
                <div
                  className={cn(
                    'transition-opacity',
                    isOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                  )}
                >
                  <Popover
                    open={isOpen}
                    onOpenChange={open => {
                      if (open) {
                        setOpenPopoverId(row.id);
                      } else {
                        setOpenPopoverId(null);
                        setShowStateSelector(null);
                        setSelectedState('');
                      }
                    }}
                  >
                    <PopoverTrigger>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="border-primary-300 bg-primary-50 text-primary-dark hover:bg-primary-100 !h-7 w-fit self-start !rounded-full border !px-2 text-xs font-medium"
                      >
                        {hasExistingApplication ? 'Continue Application' : 'Start Application'}
                        <ChevronRight className="!size-3" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="relative w-80"
                      align="start"
                    >
                      <button
                        onClick={() => {
                          setOpenPopoverId(null);
                          setShowStateSelector(null);
                          setSelectedState('');
                        }}
                        className="absolute right-2 top-2 opacity-70 hover:opacity-100"
                      >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                      </button>
                      <div className="px-2">
                        {hasExistingApplication ? (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-900">
                              Continue your application
                            </div>
                            <div className="text-xs text-gray-700">
                              State: {existingApplication.stateOfResidenceAbbreviation}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleStartApplication(row)}
                              className="bg-primary hover:bg-primary-hover w-full"
                            >
                              Continue Application
                            </Button>
                          </div>
                        ) : showSelector ? (
                          <div className="space-y-3">
                            <StateOfResidenceSelector
                              value={selectedState}
                              onChange={setSelectedState}
                              disabled={true}
                              availableStates={
                                row.policyType
                                  ? getProductAvailableStates(row.policyType)
                                  : undefined
                              }
                            />
                            <Button
                              size="sm"
                              onClick={() => handleStartApplication(row)}
                              disabled={!selectedState || isCreatingPolicyApplication}
                              className="w-full"
                            >
                              {isCreatingPolicyApplication ? (
                                <Loader className="h-4 w-4 animate-spin" />
                              ) : (
                                'Continue to Application'
                              )}
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-900">
                              Ready to apply for this policy?
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleStartApplication(row)}
                              className="bg-primary hover:bg-primary-hover w-full"
                            >
                              Start Application
                            </Button>
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>,
          );
        }

        return renderTableCell(labelContent);
      },
    }),
    columnHelper.accessor('totalCost', {
      id: 'totalCost',
      meta: { label: 'Cost' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
            showRoiTooltip
          >
            <SlideOutSheet
              title="FAQ"
              slideOutContent={<FundingSourceCostSlideOutContent />}
            >
              <span className="hover:underline">Cost</span>
            </SlideOutSheet>
          </SortHeader>,
        ),
      cell: info => {
        const row = info.row.original;
        const costValue = renderLtcCostCell(info.getValue(), {
          isTotal: row.type === 'totals',
          color: 'text-red-700',
          className: 'flex items-center gap-1.5',
        });

        const costDisplay = (
          <div className="flex flex-col gap-0.5">
            {costValue}
            {row.partnerCost != null && row.partnerCost > 0 && (
              <PartnerValueTooltip
                amountDisplay="Inclusive of Partner"
                label="Joint policies provide coverage for both partners under a single insurance policy, combining their insurance costs into one plan."
              />
            )}
          </div>
        );

        return renderTableCell(
          row.type === 'insurance' ? (
            <InlineWhiteBoxSlideOutButton
              fundingSourceId={row.fundingSourceId!}
              onOpen={openPolicyInsightSlideOut}
            >
              {costDisplay}
            </InlineWhiteBoxSlideOutButton>
          ) : (
            costDisplay
          ),
        );
      },
    }),
    columnHelper.accessor('ltcPayOut', {
      id: 'ltcPayOut',
      meta: { label: 'LTC Payout' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
            showRoiTooltip
          >
            <SlideOutSheet
              title="FAQ"
              slideOutContent={<FundingSourceLtcPayoutSlideOutContent />}
            >
              <span className="hover:underline">LTC Payout</span>
            </SlideOutSheet>
          </SortHeader>,
        ),
      cell: info => {
        const row = info.row.original;
        const payoutDisplay = (
          <div
            className={getCellClassName({
              isTotal: row.type === 'totals',
              color: 'text-green-600',
              className: 'flex flex-col gap-0.5',
            })}
          >
            <LtcCostDisplay value={info.getValue()} />
            {row.partnerLtcPayOut != null && row.partnerLtcPayOut > 0 && (
              <PartnerValueTooltip
                amountDisplay={row.partnerLtcPayOut}
                label="LTC Payout for partner"
              />
            )}
          </div>
        );

        return renderTableCell(
          row.type === 'insurance' ? (
            <InlineWhiteBoxSlideOutButton
              fundingSourceId={row.fundingSourceId!}
              onOpen={openPolicyInsightSlideOut}
            >
              {payoutDisplay}
            </InlineWhiteBoxSlideOutButton>
          ) : (
            payoutDisplay
          ),
        );
      },
    }),
    columnHelper.accessor('nonLtcPayOut', {
      id: 'nonLtcPayOut',
      meta: { label: 'Non-LTC Payout' },
      header: ({ column }) =>
        renderHeaderCell(
          <div className="flex items-center gap-0">
            <SlideOutSheet
              title="FAQ"
              slideOutContent={<FundingSourceLtcPayoutSlideOutContent />}
            >
              <span className="hover:underline">Non-LTC Payout</span>
            </SlideOutSheet>
            <ROIGuidance className="ml-1 w-max p-0.5 [&_svg]:!size-3 [&_svg]:p-0" />
          </div>,
        ),
      cell: info => {
        const row = info.row.original;
        return renderTableCell(
          renderLtcCostCell(info.getValue(), {
            isTotal: row.type === 'totals',
          }),
        );
      },
    }),
    columnHelper.accessor('totalPayOut', {
      id: 'totalPayOut',
      meta: { label: 'Total Payout' },
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="link"
            showRoiTooltip
          >
            <SlideOutSheet
              title="FAQ"
              slideOutContent={<FundingSourceTotalPayoutSlideOutContent />}
            >
              <span className="hover:underline">Total Payout</span>
            </SlideOutSheet>
          </SortHeader>,
        ),
      cell: info => {
        const row = info.row.original;
        return renderTableCell(
          renderLtcCostCell(info.getValue(), {
            isTotal: row.type === 'totals',
          }),
        );
      },
    }),
    columnHelper.accessor('projectedReturnOnInvestment', {
      id: 'roi',
      meta: {
        label: roiColumnHeaderLabel,
      },
      sortingFn: (rowA, rowB) => {
        switch (returnOnInvestmentState) {
          case 'compoundAnnualGrowthRate':
            return (
              rowA.original.projectedCompoundAnnualGrowthRate -
              rowB.original.projectedCompoundAnnualGrowthRate
            );
          case 'internalRateOfReturn':
            return (
              (rowA.original.projectedInternalRateOfReturn ?? 0) -
              (rowB.original.projectedInternalRateOfReturn ?? 0)
            );
          case 'total':
          default:
            return (
              rowA.original.projectedReturnOnInvestment - rowB.original.projectedReturnOnInvestment
            );
        }
      },
      header: ({ column }) =>
        renderHeaderCell(
          <div className="flex w-full items-center">
            <SortHeader
              column={column}
              variant="link"
              showRoiTooltip
            >
              <SlideOutSheet
                title="FAQ"
                slideOutContent={<ROISlideOutContent />}
                maxWidth="custom-roi"
              >
                <span className="hover:underline">{roiColumnHeaderLabel}</span>
              </SlideOutSheet>
            </SortHeader>
          </div>,
        ),
      cell: info => {
        const row = info.row.original;
        const formattedValue = formatROI(
          {
            projectedReturnOnInvestment: row.projectedReturnOnInvestment,
            projectedCompoundAnnualGrowthRate: row.projectedCompoundAnnualGrowthRate,
            projectedInternalRateOfReturn: row.projectedInternalRateOfReturn,
            investmentPeriodYears: row.investmentPeriodYears ?? 0,
            isIrrSuspect: row.isIrrSuspect,
          },
          returnOnInvestmentState,
        );

        const cell = renderROICell(formattedValue, {
          isTotal: row.type === 'totals',
          className: 'whitespace-nowrap',
        });

        const showIrrSuspectTooltip =
          row.isIrrSuspect && returnOnInvestmentState === 'internalRateOfReturn';
        const irrTooltipText =
          row.type === 'totals' ? IRR_SUSPECT_EXPLANATION_COMBINED : IRR_SUSPECT_EXPLANATION;

        return renderTableCell(
          showIrrSuspectTooltip ? (
            <Tooltip
              title={irrTooltipText}
              arrow
              placement="top"
              slotProps={{
                tooltip: { sx: { fontSize: '0.875rem', maxWidth: 320, padding: '8px 12px' } },
              }}
            >
              <span className="cursor-help underline decoration-dotted underline-offset-2">
                {cell}
              </span>
            </Tooltip>
          ) : (
            cell
          ),
        );
      },
    }),
    columnHelper.accessor('addedByAdvisorFullName', {
      id: 'addedByAdvisorFullName',
      meta: { label: 'Added By' },
      enableHiding: true,
      header: () => renderHeaderCell('Added By'),
      cell: info => renderTableCell(info.getValue() ?? ''),
    }),
    columnHelper.display({
      id: 'actions',
      enableHiding: false,
      meta: { label: 'Actions' },
      header: () => renderHeaderCell('Actions'),
      cell: ({ row }) => {
        const fundingSource = row.original;
        const isDeactivated = fundingSource.isDeactivated === true;
        if (fundingSource.id === 'totals') {
          if (isWizardMode) {
            return renderTableCell(
              <FundingSourceActionMenu
                actions={[
                  <DebugIrrAction
                    key="debug-irr"
                    onClick={() => setIsDebugModalOpen(true)}
                  />,
                ]}
              />,
            );
          }
          return renderTableCell(null);
        }

        const partnerActions = getPartnerActions(fundingSource);

        return renderTableCell(
          <div className="flex items-center justify-between gap-0.5 text-sm md:text-base">
            <FundingSourceActionMenu
              actions={[
                <EditAction
                  key="edit"
                  onClick={() =>
                    guardedEdit(
                      fundingSource.fundingSourceId,
                      fundingSource.policyType,
                      fundingSource.openModal,
                    )
                  }
                />,
                ...partnerActions,
                <DeleteAction
                  key="delete"
                  onClick={() => {
                    if (fundingSource.partnerAction === 'mirrored') {
                      fundingSource.deleteFundingSourceCallBack();
                    } else {
                      handleDeleteClick(fundingSource.deleteFundingSourceCallBack);
                    }
                  }}
                />,
              ]}
            />
            <button
              onClick={() => {
                toggleFundingSourceCallback(fundingSource, !isDeactivated);
              }}
              className={cn(
                'rounded-md px-4 py-1 text-sm',
                isDeactivated
                  ? 'bg-red-50 text-red-400 hover:bg-red-100'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              )}
            >
              {isDeactivated ? 'Off' : 'On'}
            </button>
          </div>,
        );
      },
    }),
  ];

  return (
    <>
      <DataTable
        className={className}
        columns={columnDefinitions as ColumnDef<FundingTableRow>[]}
        getRowId={row => row.id}
        isLoading={showTableSkeleton}
        data={data as FundingTableRow[]}
        tableId="fundingSourceTable"
        variant="outlined"
        isRowDeactivated={row => row.isDeactivated === true}
        isRowMasked={{
          fn: row =>
            clientHasMultipleJointPolicies &&
            row.isDeactivated === true &&
            row.isJointPolicy === true,
          tooltipContent: 'Only a single joint policy can be active',
        }}
        neverMaskedColumns={['fundingSourceLabel', 'actions']}
        initialColumnVisibility={{
          addedByAdvisorFullName: false,
        }}
        summaryRowId={'totals'}
        primaryButton={primaryButton}
        headerExtras={
          <>
            {isAdvisor && (
              <EdjPilotRestricted hidden>
                <SendAnonymousPUIButton />
              </EdjPilotRestricted>
            )}
            {hasActiveLtcPolicies &&
              (flags.policiesComparisonV2 ? (
                <EdjPilotRestricted>
                  <PoliciesComparisonModal>
                    <Button
                      variant="outline"
                      className="w-max"
                    >
                      <Columns3 />
                      <span className="hidden sm:block">Compare Policies</span>
                    </Button>
                  </PoliciesComparisonModal>
                </EdjPilotRestricted>
              ) : (
                <EdjPilotRestricted>
                  <PoliciesComparisonModalLegacy>
                    <Button
                      variant="outline"
                      className="w-max"
                    >
                      <Columns3 />
                      <span className="hidden sm:block">Compare Policies</span>
                    </Button>
                  </PoliciesComparisonModalLegacy>
                </EdjPilotRestricted>
              ))}
            <ReturnOnInvestmentCalculationOptions
              discountRate={clientCalculationSettings?.discountRate}
              calculationType={returnOnInvestmentState}
              onChange={setReturnOnInvestmentState}
              isButtonStyle={true}
            />
          </>
        }
        useCustomScrollbar
      />
      <PolicyInsightSlideOutSheet
        fundingSourceId={activePolicyInsightSheetFundingSourceId ?? undefined}
        open={activePolicyInsightSheetFundingSourceId !== null}
        onClose={closePolicyInsightSlideOut}
      />
      <FundingSourceDeleteDialogs
        showDeleteConfirmation={showDeleteConfirmation}
        handleCancelDelete={handleCancelDelete}
        handleConfirmDelete={handleConfirmDelete}
        showDeleteBlockedDialog={showDeleteBlockedDialog}
        setShowDeleteBlockedDialog={setShowDeleteBlockedDialog}
        deleteBlockedReason={deleteBlockedReason}
        showEditBlockedDialog={showEditBlockedDialog}
        setShowEditBlockedDialog={setShowEditBlockedDialog}
        editBlockedReason={editBlockedReason}
        showJointPolicyDeleteDialog={showJointPolicyDeleteDialog}
        handleJointPolicyDeleteCancel={handleJointPolicyDeleteCancel}
        handleJointPolicyDeleteConfirm={handleJointPolicyDeleteConfirm}
        jointPolicyDeleteMode={jointPolicyDeleteMode}
        setJointPolicyDeleteMode={setJointPolicyDeleteMode}
        clientName={client.clientFirstName}
        partnerName={client.mutableClientPartner?.clientFirstName}
        showMirroredDeleteDialog={showMirroredDeleteDialog}
        handleMirroredDeleteCancel={handleMirroredDeleteCancel}
        handleMirroredDeleteConfirm={handleMirroredDeleteConfirm}
        mirroredDeleteMode={mirroredDeleteMode}
        setMirroredDeleteMode={setMirroredDeleteMode}
      />
      <IrrCashflowDebugModal
        open={isDebugModalOpen}
        onClose={() => setIsDebugModalOpen(false)}
        client={client}
      />
      {renamePolicyFundingSourceId &&
        multipleFundingSources.ltcPolicy[renamePolicyFundingSourceId] && (
          <RenamePolicyDialog
            open={true}
            onClose={() => setRenamePolicyFundingSourceId(null)}
            clientId={client.clientId}
            fundingSourceId={renamePolicyFundingSourceId}
            policy={multipleFundingSources.ltcPolicy[renamePolicyFundingSourceId]}
            multipleFundingSources={multipleFundingSources}
          />
        )}
    </>
  );
}

function getPartnerActions(row: FundingTableRow): JSX.Element[] {
  const actions: JSX.Element[] = [];
  if (row.onMirrorToPartner && row.partnerFirstName) {
    actions.push(
      <MirrorToPartnerAction
        key="mirror"
        partnerFirstName={row.partnerFirstName}
        onClick={row.onMirrorToPartner}
      />,
    );
  }
  if (row.onUnlink) {
    actions.push(
      <UnlinkAction
        key="unlink"
        onClick={row.onUnlink}
      />,
    );
  }
  return actions;
}

function checkForMultipleJointPolicies(multipleFundingSources: MultipleFundingSources): boolean {
  return Object.values(multipleFundingSources.ltcPolicy).some(
    policy => policy.policyType && fundingPolicyTypeDefs[policy.policyType].isJoint,
  );
}

function getSelfFundingName(name?: string | null) {
  const defaultName = 'Self-funding';
  return !name || name === defaultName ? defaultName : `${defaultName} (${name})`;
}

function createSelfFundingRows({
  selfFundingSources,
  allPhaseInflatedProfessionalShareCost,
  setOpenModal,
  onDelete,
  onMirrorToPartner,
  onUnlink,
  partnerFirstName,
}: {
  selfFundingSources: { [fundingSourceId: string]: SelfFundingSource };
  allPhaseInflatedProfessionalShareCost: number;
  setOpenModal: FundingSourceTableProps['setOpenModal'];
  onDelete: (id: string) => void;
  onMirrorToPartner?: (sourceId: string, source: SelfFundingSource) => void;
  onUnlink?: (sourceId: string, source: SelfFundingSource) => void;
  partnerFirstName?: string;
}): FundingTableRow[] {
  return Object.entries(selfFundingSources)
    .sort(([, a], [, b]) => (b.selfFundingTotalValue ?? 0) - (a.selfFundingTotalValue ?? 0))
    .map(([fundingSourceId, selfFunding]) => {
      const areSelfFundingFieldsPopulated =
        (selfFunding.annualRateOfReturn && selfFunding.annualRateOfReturn > 0) ||
        (selfFunding.monthlyContributionAmount && selfFunding.monthlyContributionAmount > 0) ||
        (selfFunding.existingAssetsValue && selfFunding.existingAssetsValue > 0);

      const hasSelfFunding = selfFunding.hasSelfFunding || areSelfFundingFieldsPopulated;

      const isMirrored = selfFunding.partnerAction === 'mirrored';

      return {
        id: fundingSourceId,
        type: 'selfFunding' as const,
        show: hasSelfFunding,
        fundingSourceId,
        fundingSourceLabel: getSelfFundingName(selfFunding?.selfFundingName),
        totalCost: selfFunding.selfFundingTotalCost ?? 0,
        ltcPayOut: selfFunding.selfFundingProjectedCostCoverage ?? 0,
        ltcCostCoveragePercent:
          (selfFunding.selfFundingProjectedCostCoverage ?? 0) /
          allPhaseInflatedProfessionalShareCost,
        nonLtcPayOut: selfFunding.selfFundingNonLtcPayout ?? 0,
        totalPayOut: selfFunding.selfFundingTotalValue ?? 0,
        projectedReturnOnInvestment: selfFunding.selfFundingProjectedReturnOnInvestment ?? 0,
        projectedCompoundAnnualGrowthRate:
          selfFunding.selfFundingProjectedCompoundAnnualGrowthRate ?? 0,
        projectedInternalRateOfReturn: selfFunding.selfFundingProjectedInternalRateOfReturn,
        isIrrSuspect: selfFunding.isIrrSuspect,
        investmentPeriodYears: selfFunding.investmentPeriodYears ?? 0,
        openModal: () => setOpenModal.selfFunding(fundingSourceId),
        deleteFundingSourceCallBack: () => onDelete(fundingSourceId),
        addedByAdvisorFullName: selfFunding.addedByAdvisorFullName,
        isDeactivated: selfFunding.isDeactivated,
        partnerAction: isMirrored ? ('mirrored' as const) : null,
        splitRatio: isMirrored ? (selfFunding.splitRatio ?? null) : null,
        partnerFirstName,
        onMirrorToPartner: !isMirrored && onMirrorToPartner
          ? () => onMirrorToPartner(fundingSourceId, selfFunding as SelfFundingSource)
          : undefined,
        onUnlink: isMirrored && onUnlink
          ? () => onUnlink(fundingSourceId, selfFunding as SelfFundingSource)
          : undefined,
      };
    })
    .filter(row => row.show);
}

function createInsuranceRows({
  policies,
  clientHasStartedLtc,
  allPhaseInflatedProfessionalShareCost,
  setOpenModal,
  onDelete,
  onRename,
  mutableClientPartner,
}: {
  policies: { [fundingSourceId: string]: PolicyFundingSource };
  clientHasStartedLtc: boolean;
  allPhaseInflatedProfessionalShareCost: number;
  setOpenModal: FundingSourceTableProps['setOpenModal'];
  onDelete: (id: string) => void;
  onRename: (id: string) => void;
  mutableClientPartner: Client | null;
}): FundingTableRow[] {
  return Object.entries(policies)
    .sort(([, a], [, b]) => (b.policyTotalValue ?? 0) - (a.policyTotalValue ?? 0))
    .map(([fundingSourceId, policy]) => {
      const sourceQuoteProductName = readSourceQuoteField(policy.sourceQuote, 'productName');
      const productName =
        typeof sourceQuoteProductName === 'string' ? sourceQuoteProductName : undefined;

      // Get underwriting status for this policy
      const isJoint =
        !isNullOrUndefined(policy.policyType) && fundingPolicyTypeDefs[policy.policyType].isJoint;

      const isEligibleForApplication = isPolicyEligibleForApplication(policy);

      const partnerPolicy =
        isJoint && mutableClientPartner
            ? mutableClientPartner.multipleFundingSources?.ltcPolicy[fundingSourceId]
          : null;

      return {
        id: fundingSourceId,
        type: 'insurance',
        show: policy.hasPolicyFunding,
        fundingSourceId,
        fundingSourceLabel: getPolicyName(policy),
        totalCost: policy.policyTotalCost ?? 0,
        ltcPayOut: clientHasStartedLtc
          ? (policy.policyCoverageOfRemainingLtcCosts ?? 0)
          : (policy.projectedCostCoverage ?? 0),
        ltcCostCoveragePercent:
          (clientHasStartedLtc
            ? (policy.policyCoverageOfRemainingLtcCosts ?? 0)
            : (policy.projectedCostCoverage ?? 0)) / allPhaseInflatedProfessionalShareCost,
        nonLtcPayOut: policy.policyNonLtcPayout ?? 0,
        totalPayOut: policy.policyTotalValue ?? 0,
        partnerLtcPayOut: partnerPolicy
          ? (partnerPolicy.projectedCostCoverage ??
            partnerPolicy.policyCoverageOfRemainingLtcCosts ??
            0)
          : undefined,
        partnerCost: partnerPolicy ? (partnerPolicy.policyTotalCost ?? 0) : undefined,
        projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment ?? 0,
        projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate ?? 0,
        investmentPeriodYears: policy.investmentPeriodYears ?? 0,
        projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
        isIrrSuspect: policy.isIrrSuspect,
        openModal: () => setOpenModal.ltcPolicy(policy.policyType, fundingSourceId),
        deleteFundingSourceCallBack: () => onDelete(fundingSourceId),
        isDeactivated: policy.isDeactivated,
        addedByAdvisorFullName: policy.addedByAdvisorFullName,
        addedViaShopInsurance: policy.addedViaShopInsurance,
        isJointPolicy: isJoint,
        policyAddedAt: policy.policyAddedAt,
        productName,
        quoteId: policy.insuranceQuoteId,
        policyType: policy.policyType,
        isEligibleForApplication: isEligibleForApplication ?? undefined,
        onRename: () => onRename(fundingSourceId),
      };
    });
}

function createAnnuityRows({
  annuity,
  allPhaseInflatedProfessionalShareCost,
  setOpenModal,
  onDelete,
}: {
  annuity: AnnuitySource;
  allPhaseInflatedProfessionalShareCost: number;
  setOpenModal: FundingSourceTableProps['setOpenModal'];
  onDelete: () => void;
}): FundingTableRow[] {
  if (!annuity.hasAnnuity) return [];

  return [
    {
      id: 'annuity',
      type: 'annuity',
      show: true,
      fundingSourceLabel: 'Annuity',
      totalCost: annuity.annuityTotalCost ?? 0,
      ltcPayOut: annuity.annuityProjectedCostCoverage ?? 0,
      ltcCostCoveragePercent:
        (annuity.annuityProjectedCostCoverage ?? 0) / allPhaseInflatedProfessionalShareCost,
      nonLtcPayOut: annuity.annuityNonLtcPayout ?? 0,
      totalPayOut: annuity.annuityTotalValue ?? 0,
      projectedReturnOnInvestment: annuity.annuityProjectedReturnOnInvestment ?? 0,
      projectedCompoundAnnualGrowthRate: annuity.annuityProjectedCompoundAnnualGrowthRate ?? 0,
      investmentPeriodYears: annuity.investmentPeriodYears ?? 0,
      projectedInternalRateOfReturn: annuity.annuityProjectedInternalRateOfReturn,
      isIrrSuspect: annuity.isIrrSuspect,
      openModal: setOpenModal.annuity,
      deleteFundingSourceCallBack: onDelete,
      addedByAdvisorFullName: annuity.addedByAdvisorFullName,
      isDeactivated: annuity.isDeactivated,
    },
  ];
}

function createTotalsRow({
  combinedFundingTotalCost,
  combinedProjectedCostCoverage,
  combinedNonLtcPayout,
  combinedTotalValue,
  combinedProjectedReturnOnInvestment,
  combinedProjectedCompoundAnnualGrowthRate,
  combinedProjectedInternalRateOfReturn,
  combinedIsIrrSuspect,
  combinedInvestmentPeriodYears,
}: {
  combinedFundingTotalCost: number;
  combinedProjectedCostCoverage: number;
  combinedNonLtcPayout: number;
  combinedTotalValue: number;
  combinedProjectedReturnOnInvestment: number;
  combinedProjectedCompoundAnnualGrowthRate: number;
  combinedProjectedInternalRateOfReturn: number | undefined;
  combinedIsIrrSuspect?: boolean;
  combinedInvestmentPeriodYears: number;
}) {
  return {
    id: 'totals',
    type: 'totals' as const,
    show: true,
    fundingSourceLabel: 'Total',
    totalCost: combinedFundingTotalCost,
    ltcPayOut: combinedProjectedCostCoverage,
    nonLtcPayOut: combinedNonLtcPayout,
    totalPayOut: combinedTotalValue,
    projectedReturnOnInvestment: combinedProjectedReturnOnInvestment,
    projectedCompoundAnnualGrowthRate: combinedProjectedCompoundAnnualGrowthRate,
    projectedInternalRateOfReturn: combinedProjectedInternalRateOfReturn,
    isIrrSuspect: combinedIsIrrSuspect,
    ltcCostCoveragePercent: 0, // Not needed for totals
    openModal: () => {}, // Not needed for totals
    deleteFundingSourceCallBack: () => {}, // Not needed for totals
    investmentPeriodYears: combinedInvestmentPeriodYears,
  };
}

type InlineWhiteBoxSlideOutButtonProps = {
  fundingSourceId: string;
  labelColor?: string;
  labelHoverColor?: string;
  children?: React.ReactNode;
  onOpen: (fundingSourceId: string) => void;
};
type PolicyInsightView = 'monthly' | 'legacyText' | 'legacyStructured';

function InlineWhiteBoxSlideOutButton({
  labelColor,
  fundingSourceId,
  children,
  onOpen,
}: InlineWhiteBoxSlideOutButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        'line-clamp-2 max-w-[220px] whitespace-normal break-words text-left hover:underline',
        labelColor ? labelColor : '',
      )}
      onClick={() => onOpen(fundingSourceId)}
    >
      {children}
      <span className="sr-only">Learn more</span>
    </button>
  );
}

function PolicyInsightSlideOutSheet({
  fundingSourceId,
  open,
  onClose,
}: {
  fundingSourceId?: string;
  open: boolean;
  onClose: () => void;
}) {
  const [view, setView] = useState<PolicyInsightView>('monthly');
  const hasProjectionView = useHasProjectionView(fundingSourceId);
  const showMonthly = hasProjectionView && view === 'monthly' && !!fundingSourceId;

  useEffect(() => {
    if (open) {
      setView('monthly');
    }
  }, [fundingSourceId, open]);

  return (
    <SheetModal
      open={open}
      onClose={onClose}
      title="Waterlily Policy Insight"
      subTitle=""
      className={getPolicyInsightWidthClassName(showMonthly)}
    >
      <div className="mt-0 flex flex-col justify-between gap-2 text-gray-800">
        {fundingSourceId && (
          <PolicyInsightSlideOutContent
            fundingSourceId={fundingSourceId}
            view={view}
            setView={setView}
            hasProjectionView={hasProjectionView}
            showMonthly={showMonthly}
          />
        )}
      </div>
    </SheetModal>
  );
}

function PolicyInsightSlideOutContent({
  fundingSourceId,
  view,
  setView,
  hasProjectionView,
  showMonthly,
}: {
  fundingSourceId: string;
  view: PolicyInsightView;
  setView: (view: PolicyInsightView) => void;
  hasProjectionView: boolean;
  showMonthly: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      {hasProjectionView && (
        <div className="mt-8 bg-white pb-2">
          <Select
            value={view}
            onValueChange={v => setView(v as PolicyInsightView)}
          >
            <SelectTrigger className="w-[260px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly breakdown</SelectItem>
              <SelectItem value="legacyText">Legacy (text view)</SelectItem>
              <SelectItem value="legacyStructured">Legacy (structured view)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div
        className={cn(
          !hasProjectionView && 'mt-6',
          hasProjectionView && view === 'legacyText' && 'mt-10',
        )}
      >
        {showMonthly ? (
          <ProjectionDetailsContent fundingSourceId={fundingSourceId} />
        ) : hasProjectionView && view === 'legacyStructured' ? (
          <PolicyInsight fundingSourceId={fundingSourceId} />
        ) : (
          <InsuranceWhiteBoxSlideOutContent fundingSourceId={fundingSourceId} />
        )}
      </div>
    </div>
  );
}

function getPolicyInsightWidthClassName(showMonthly: boolean) {
  return showMonthly
    ? 'sm:min-w-[96%] md:min-w-[95%] lg:min-w-[1700px]'
    : 'sm:min-w-[85%] md:min-w-[600px] lg:min-w-[700px]';
}

function PartnerValueTooltip({
  amountDisplay,
  label,
}: {
  amountDisplay: number | string;
  label: string;
}) {
  const roundingStep = useLtcCostDisplayPrecision();
  const isNumberDisplay = typeof amountDisplay === 'number';
  const { formattedDisplay } = formatLtcCostDisplay(isNumberDisplay ? amountDisplay : undefined, {
    roundingStep,
  });
  return (
    <Tooltip
      arrow={true}
      placement="right"
      title={<p className="text-sm">{label}</p>}
    >
      <span className="flex items-center gap-0.5 text-xs text-gray-700">
        {isNumberDisplay && formattedDisplay ? (
          <>
        {formattedDisplay}
        <CgAsterisk />
          </>
        ) : (
          <>{amountDisplay}</>
        )}
      </span>
    </Tooltip>
  );
}
